import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PlanHeader from "../features/workout/workout-plan/components/plan-header";
import { Alert, Box, Button, Grid, Tab, Tabs, Typography } from "@mui/material";
import { createWorkoutPlan, fetchActivePlan, selectActivePlan, selectIsLoading, updateWorkoutPlan } from "../features/workout/workout-plan/workout-plan.slice";
import { useEffect, useState } from "react";
import Loader from "../components/loader";
import dayjs from "dayjs";
import { showNotification } from "../features/alerts/alert.slice";
import { NotificationType } from "../shared/enums";
import PlanBuilderActionButtons from "../features/workout/workout-plan/components/plan-builder-action-buttons";
import { ExerciseRequest, WorkoutDayExerciseRequest, WorkoutDayRequest, WorkoutPlanRequest } from "../service/model/workout.model";
import { createExercise } from "../features/workout/exercise/exercise.slice";
import ExerciseForm from "../features/workout/workout-plan/components/exercise-form";
import WorkoutDay from "../features/workout/workout-plan/components/workout-day";
import { Member } from "../service/model/member.model";
import { getMember } from "../service/member.service";
import { selectLoggedInUser } from "../features/auth/auth.slice";
import { isBusinessUser } from "../shared/utils";

export type HeaderData = {
    startDate: string;
    endDate: string;
}

const WorkoutPlanBuilder = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectIsLoading);
    const activePlan = useAppSelector(selectActivePlan);
    const loggedInUser = useAppSelector(selectLoggedInUser);

    const { memberId } = useParams<{ memberId: string }>();
    const [showExerciseForm, setShowExerciseForm] = useState(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [member, setMember] = useState<Member | null>(null);
    const [memberLoading, setMemberLoading] = useState(false);

    useEffect(() => {
        if (memberId) {
            dispatch(fetchActivePlan(Number(memberId)));
        }
    }, [memberId, dispatch]);

    useEffect(() => {
        let mounted = true;
        if (!memberId) {
            setMember(null);
            return;
        }
        setMemberLoading(true);
        getMember(Number(memberId))
            .then(m => {
                if (mounted) setMember(m);
            })
            .catch(err => {
                console.error("Failed to fetch member details:", err);
            })
            .finally(() => {
                if (mounted) setMemberLoading(false);
            });
        return () => { mounted = false; };
    }, [memberId]);

    useEffect(() => {
        if (activePlan?.cycleLength && selectedTab >= activePlan.cycleLength) {
            setSelectedTab(activePlan.cycleLength - 1);
        }
    }, [activePlan?.cycleLength, selectedTab]);

    const handleClose = () => {
        navigate(-1);
    };

    const handleExerciseSubmit = (data: ExerciseRequest) => {
        dispatch(createExercise({ ...data, })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                setShowExerciseForm(false);
            }
        });
    };

    const handleSave = async () => {
        if (!activePlan?.workoutDays || activePlan?.workoutDays?.length === 0) {
            dispatch(
                showNotification({
                    message: "At least one exercise required. Please add exercises to the plan before saving.",
                    type: NotificationType.Error,
                })
            );
            return;
        }
        if (!activePlan.cycleLength || activePlan.cycleLength < 1) {
            dispatch(
                showNotification({
                    message: "Cycle length must be at least 1. Please set a valid cycle length before saving.",
                    type: NotificationType.Error,
                })
            );
            return;
        }
        const dayWithNoExercises = activePlan.workoutDays.find(day => !day.exercises || day.exercises.length === 0);
        if (dayWithNoExercises) {
            dispatch(
                showNotification({
                    message: `All days must have at least one exercise. Day ${dayWithNoExercises.dayIndex} has no exercises.`,
                    type: NotificationType.Error,
                })
            );
            return;
        }

        // const exerciseWithInvalidSetsOrReps = activePlan.workoutDays
        //     .flatMap(day => day.exercises || [])
        //     .find(exercise => !exercise.sets || !exercise.reps);
        // if (exerciseWithInvalidSetsOrReps) {
        //     dispatch(
        //         showNotification({
        //             message: "All exercises must have a valid number of sets and reps.",
        //             type: NotificationType.Error,
        //         })
        //     );
        //     return;
        // }

        if (member) {
            const request: WorkoutPlanRequest = {
                id: activePlan?.id,
                name: activePlan?.name ?? `Workout Plan - ${dayjs().format('YYYY-MM-DD')}`,
                description: activePlan?.description ?? '',
                memberId: member.id,
                workoutDays: activePlan?.workoutDays?.map(day => ({
                    id: day.id,
                    dayIndex: day.dayIndex,
                    workoutPlanId: activePlan?.id,
                    name: day.name,
                    notes: day.notes,
                    exercises: day.exercises?.map(exercise => ({
                        id: exercise.id,
                        workoutDayId: day.id,
                        exerciseId: exercise.exercise?.id,
                        orderIndex: exercise.orderIndex,
                        sets: exercise.sets,
                        reps: exercise.reps,
                        targetWeight: exercise.targetWeight,
                        targetDuration: exercise.targetDuration,
                        restSeconds: exercise.restSeconds,
                        notes: exercise.notes,
                    } as WorkoutDayExerciseRequest)) ?? [],
                } as WorkoutDayRequest)) ?? [],
                startDate: activePlan?.startDate ?? dayjs().format('YYYY-MM-DD'),
                endDate: activePlan?.endDate ?? dayjs().add(30, 'day').format('YYYY-MM-DD'),
                cycleLength: activePlan?.cycleLength ?? activePlan?.workoutDays?.length ?? 1,
            };
            if (activePlan?.id) {
                await dispatch(updateWorkoutPlan(request));
            } else {
                await dispatch(createWorkoutPlan(request));
            }
        }
    };

    return (
        <div>
            {(isLoading || memberLoading) ? (
                <Loader />
            ) : (
                <>
                    {member ? (
                        <>
                            <Typography variant="h5" gutterBottom>
                                Workout Plan
                            </Typography>

                            <Grid container direction="row" justifyContent="flex-end" spacing={2}>
                                <Button variant="contained" color="primary" onClick={() => setShowExerciseForm(true)} sx={{ mb: 2 }}>
                                    New Exercise
                                </Button>
                                <Button variant="contained" color="primary" onClick={() => navigate("/exercises")} sx={{ mb: 2 }}>
                                    Manage Exercises
                                </Button>
                            </Grid>
                            {!activePlan?.id && isBusinessUser(loggedInUser?.user) && (
                                <Alert severity="info" variant="filled" sx={{ width: 'fit-content', mb: 2, justifyContent: 'center' }}>
                                    This member does not have an active workout plan. Please create a new plan by adding exercises and saving.
                                </Alert>
                            )}
                            <PlanHeader member={member} />

                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(event, newValue) => setSelectedTab(newValue)}
                                    variant="fullWidth"
                                    sx={{
                                        '& .MuiTabs-flexContainer': {
                                            flexWrap: 'wrap',
                                        },
                                    }}
                                >
                                    {Array.from({ length: activePlan?.cycleLength ?? activePlan?.workoutDays?.length ?? 1 }, (_, i) => (
                                        <Tab key={i} label={`Day ${i + 1}`} id={`tab-${i}`} aria-controls={`tabpanel-${i}`} />
                                    ))}
                                </Tabs>
                            </Box>
                            {Array.from({ length: activePlan?.cycleLength ?? 1 }, (_, index) => (
                                <div key={index} role="tabpanel" id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} hidden={selectedTab !== index}>
                                    {selectedTab === index && <WorkoutDay dayIndex={index} />}
                                </div>
                            ))}
                            <PlanBuilderActionButtons onClose={handleClose} onSave={handleSave} />
                            {showExerciseForm &&
                                <ExerciseForm exercise={undefined} editable={true} onClose={() => setShowExerciseForm(false)} onSubmit={handleExerciseSubmit} />
                            }
                        </>
                    ) : (
                        <Alert severity="error" variant="filled">
                            Member not selected or you are not authorized to view this member's workout plan.
                        </Alert>
                    )}
                </>
            )}
        </div>
    )
}

export default WorkoutPlanBuilder;